import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
// Checkbox removed - sales tracking moved to Sales page
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Plus, Calendar, Scale, MapPin, Trash2, TrendingUp, Package, BarChart3, X, DollarSign, Edit2, Save, Wheat, FileText } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';
import CollapsibleSection from './ui/CollapsibleSection';
import { ApiaryYieldComparison } from './harvest/ApiaryYieldComparison';
import { downloadHarvestReport } from '../utils/harvestReportPdf';
import { toUnit, displayUnit } from '../utils/harvestStats';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Year color palette for the bar chart
const YEAR_COLORS = [
  '#f59e0b', // amber-500
  '#10b981', // emerald-500
  '#3b82f6', // blue-500
  '#8b5cf6', // violet-500
  '#ef4444', // red-500
  '#06b6d4', // cyan-500
  '#f97316', // orange-500
  '#84cc16', // lime-500
];

const HarvestsView = ({ token, hives, yards, summary, onAddHarvest, onRefresh, refreshKey }) => {
  const { user } = useAuth();
  const wUnit = displayUnit(summary?.default_unit);
  const [reportBusy, setReportBusy] = useState(false);
  const [allHarvests, setHarvests] = useState([]);
  const [yardFilter, setYardFilter] = useState('all');
  const [knownYears, setKnownYears] = useState([]);
  const [allPendingHarvests, setPendingHarvests] = useState([]);
  const hiveYard = (id) => (hives || []).find(h => h.id === id)?.yard || '';
  const harvests = React.useMemo(() => (yardFilter === 'all' ? allHarvests : allHarvests.filter(h => h.yard === yardFilter || (!h.yard && [...(h.hive_ids || []), h.hive_id].some(id => id && hiveYard(id) === yardFilter)))), [allHarvests, yardFilter, hives]);
  const pendingHarvests = yardFilter === 'all' ? allPendingHarvests : allPendingHarvests.filter(p => (p.yard || hiveYard(p.hive_id)) === yardFilter);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState('');
  const [yearlyData, setYearlyData] = useState([]);
  const [config, setConfig] = useState(null);
  
  // Edit harvest state
  const [editingHarvest, setEditingHarvest] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Pending harvests (supers removed, awaiting recording)
  const [recordingHarvest, setRecordingHarvest] = useState(null);
  const [recordFormData, setRecordFormData] = useState({});
  const [savingRecord, setSavingRecord] = useState(false);

  // Get honey types and quality grades from config for edit dialog
  const honeyTypes = config?.honey_types || [];
  const qualityGrades = config?.quality_grades || [];

  useEffect(() => {
    fetchHarvests();
    fetchConfig();
    fetchPendingHarvests();
  }, [selectedYear, refreshKey]);

  const fetchConfig = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/harvest-config`, { headers });
      setConfig(res.data);
    } catch (err) {
    }
  };

  const fetchPendingHarvests = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/pending-harvests`, { headers });
      setPendingHarvests(res.data || []);
    } catch (err) {
    }
  };

  const openRecordDialog = (pending) => {
    setRecordingHarvest(pending);
    setRecordFormData({
      date: pending.date_removed || new Date().toISOString().split('T')[0],
      harvest_year: summary?.current_year || String(new Date().getFullYear()),
      honey_type: '',
      quantity: '',
      unit: summary?.default_unit || 'kg',
      frames_harvested: '',
      quality_grade: '',
      notes: `${pending.quantity} x ${pending.super_type} removed from Hive ${pending.hive_number}`,
      sale_price: '',
    });
  };

  const handleRecordHarvest = async () => {
    if (!recordingHarvest) return;
    setSavingRecord(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/pending-harvests/${recordingHarvest.id}/record`, recordFormData, { headers });
      toast.success('Harvest recorded!');
      setRecordingHarvest(null);
      fetchPendingHarvests();
      fetchHarvests();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to record harvest');
    } finally {
      setSavingRecord(false);
    }
  };

  const handleDismissPending = async (pendingId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/pending-harvests/${pendingId}`, { headers });
      toast.success('Entry dismissed');
      fetchPendingHarvests();
    } catch (err) {
      toast.error('Failed to dismiss');
    }
  };

  // Re-fetch harvests when summary changes (indicates data was updated elsewhere)
  useEffect(() => {
    if (summary) {
      fetchHarvests();
    }
  }, [summary?.total_harvested, summary?.total_frames]);

  useEffect(() => {
    // Calculate yearly data for chart when harvests change
    if (harvests.length > 0) {
      const byYear = {};
      harvests.forEach(h => {
        const year = h.harvest_year || 'Unknown';
        if (!byYear[year]) {
          byYear[year] = { year, total: 0, count: 0, frames: 0, revenue: 0 };
        }
        byYear[year].total += toUnit(h, wUnit);
        byYear[year].count += 1;
        byYear[year].frames += h.frames_harvested || 0;
        if (h.sale_price && h.quantity) {
          byYear[year].revenue += (parseFloat(h.quantity) * parseFloat(h.sale_price));
        }
      });
      const data = Object.values(byYear).sort((a, b) => a.year.localeCompare(b.year));
      setYearlyData(data);
    } else {
      setYearlyData([]);
    }
  }, [harvests, wUnit]);

  // Calculate revenue metrics
  const revenueMetrics = React.useMemo(() => {
    const currentYear = summary?.current_year || '';
    let totalRevenue = 0;
    let soldRevenue = 0;
    let potentialRevenue = 0;
    let soldCount = 0;
    let inStockCount = 0;
    let currentSeasonRevenue = 0;
    
    harvests.forEach(h => {
      const revenue = (parseFloat(h.quantity) || 0) * (parseFloat(h.sale_price) || 0);
      totalRevenue += revenue;
      
      if (h.is_sold) {
        soldRevenue += revenue;
        soldCount += 1;
      } else {
        potentialRevenue += revenue;
        inStockCount += 1;
      }
      
      if (h.harvest_year === currentYear) {
        currentSeasonRevenue += revenue;
      }
    });
    
    return {
      totalRevenue,
      soldRevenue,
      potentialRevenue,
      soldCount,
      inStockCount,
      currentSeasonRevenue,
      currentYear
    };
  }, [harvests, summary]);

  const fetchHarvests = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = selectedYear ? { year: selectedYear } : {};
      const res = await axios.get(`${API}/harvests`, { headers, params });
      setHarvests(res.data || []);
      setKnownYears(prev => [...new Set([...prev, ...(res.data || []).map(h => h.harvest_year)])]);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (harvestId) => {
    if (!window.confirm('Are you sure you want to delete this harvest record?')) return;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/harvests/${harvestId}`, { headers });
      toast.success('Harvest deleted');
      fetchHarvests();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to delete harvest');
    }
  };

  // Edit harvest functions
  const openEditDialog = (harvest) => {
    setEditingHarvest(harvest);
    setEditFormData({
      quantity: harvest.quantity || '',
      unit: harvest.unit || 'kg',
      honey_type: harvest.honey_type || '',
      quality_grade: harvest.quality_grade || '',
      notes: harvest.notes || '',
      storage_location: harvest.storage_location || '',
    });
  };

  const saveHarvestEdit = async () => {
    if (!editingHarvest) return;
    setSavingEdit(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/harvests/${editingHarvest.id}`, editFormData, { headers });
      toast.success('Harvest updated');
      setEditingHarvest(null);
      fetchHarvests();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to update harvest');
    } finally {
      setSavingEdit(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const years = [...new Set([...knownYears, selectedYear])].filter(Boolean).sort().reverse();
  const currentSeasonHarvests = harvests.filter(h => h.harvest_year === summary?.current_year);
  const currentSeasonTotal = Math.round(currentSeasonHarvests.reduce((sum, h) => sum + toUnit(h, wUnit), 0) * 10) / 10;
  const allTimeTotal = Math.round(harvests.reduce((sum, h) => sum + toUnit(h, wUnit), 0) * 10) / 10;
  const allTimeFrames = harvests.reduce((sum, h) => sum + (h.frames_harvested || 0), 0);

  return (
    <div className="space-y-4">
      {/* Header with Add Button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
          <Wheat className="h-6 w-6 text-amber-600" /> Harvests
        </h2>
        <div className="flex items-center gap-2 flex-wrap sm:justify-end">
          {(yards || []).length > 0 && (
            <div className="flex items-center gap-1.5" data-testid="harvest-yard-filter">
              <MapPin className="h-4 w-4 text-stone-400" />
              <Select value={yardFilter} onValueChange={setYardFilter}>
                <SelectTrigger data-testid="harvest-yard-filter-select" className="w-[170px] h-9 text-sm">
                  <SelectValue placeholder="All Apiaries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Apiaries</SelectItem>
                  {yards.map(y => <SelectItem key={y.id} value={y.name}>{y.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button
            variant="outline"
            data-testid="harvest-report-btn"
            disabled={reportBusy || harvests.length === 0}
            onClick={async () => {
              setReportBusy(true);
              try {
                await downloadHarvestReport({
                  unit: wUnit,
                  harvests,
                  yieldHarvests: allHarvests,
                  hives: hives || [],
                  apiaryLabel: yardFilter === 'all' ? 'All Apiaries' : yardFilter,
                  seasonLabel: selectedYear || 'All Seasons',
                  ownerName: user?.name
                });
                toast.success('Harvest report downloaded');
              } catch {
                toast.error('Failed to create report');
              } finally {
                setReportBusy(false);
              }
            }}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            <FileText className="h-4 w-4 mr-2" />
            {reportBusy ? 'Creating…' : 'Harvest Report'}
          </Button>
          <Button onClick={onAddHarvest} className="bg-amber-500 hover:bg-amber-600" data-testid="add-harvest-btn">
            <Plus className="h-4 w-4 mr-2" />
            Record Harvest
          </Button>
        </div>
      </div>

      {/* Supers Ready for Harvest */}
      {pendingHarvests.length > 0 && (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-300 rounded-xl p-4" data-testid="supers-ready-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-orange-800 flex items-center gap-2">
              <Package className="h-5 w-5" />
              Supers Ready for Harvest
              <span className="text-xs bg-orange-200 text-orange-700 px-2 py-0.5 rounded-full">{pendingHarvests.length}</span>
            </h3>
          </div>
          <div className="grid gap-2">
            {pendingHarvests.map((p) => (
              <div key={p.id} className="bg-white rounded-lg border border-orange-200 p-3 flex items-center justify-between" data-testid={`pending-harvest-${p.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-stone-800">Hive {p.hive_number}</span>
                    {p.yard && <span className="text-xs text-stone-400 flex items-center gap-1"><MapPin className="h-3 w-3" />{p.yard}</span>}
                  </div>
                  <div className="text-sm text-stone-600 mt-0.5">
                    <span className="font-medium text-orange-700">{p.quantity}x {p.super_type}</span>
                    <span className="text-stone-400 ml-2">removed {new Date(p.date_removed).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => openRecordDialog(p)}
                    className="bg-amber-500 hover:bg-amber-600 text-white text-xs"
                    data-testid={`record-harvest-btn-${p.id}`}
                  >
                    <Scale className="h-3 w-3 mr-1" />
                    Record Harvest
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDismissPending(p.id)}
                    className="text-stone-400 hover:text-red-500 h-8 w-8 p-0"
                    data-testid={`dismiss-pending-${p.id}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Stats Banner */}
      <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-amber-700">{currentSeasonTotal} {wUnit}</div>
            <div className="text-xs text-amber-600">This Season</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-stone-700">{allTimeTotal} {wUnit}</div>
            <div className="text-xs text-stone-500">All Time</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-yellow-600">{allTimeFrames}</div>
            <div className="text-xs text-yellow-500">Frames Harvested</div>
          </div>
        </div>
      </div>

      {allHarvests.length > 0 && (
        <ApiaryYieldComparison
          harvests={allHarvests}
          hives={hives || []}
          unit={wUnit}
          highlight={yardFilter !== 'all' ? yardFilter : null}
          subtitle={selectedYear ? `Season ${selectedYear}` : 'All seasons'}
        />
      )}

      {/* Collapsible Sections */}
      <div className="space-y-3">
        
        {/* Harvest Overview Section - Per Harvest with KG */}
        <CollapsibleSection 
          title="Harvest Overview" 
          icon={BarChart3} 
          defaultOpen={true}
          badge={`${harvests.length} harvests`}
          badgeColor="amber"
        >
          <div className="pt-4 space-y-4">
            {/* Per-Harvest Bar Chart */}
            {harvests.length > 0 && (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={harvests.slice(0, 12).map((h, idx) => ({
                      label: `${formatDate(h.date).slice(0, 6)}${harvests.filter((hh, i) => i < idx && hh.date === h.date).length > 0 ? ` (${idx + 1})` : ''}`,
                      quantity: Math.round(toUnit(h, wUnit) * 10) / 10,
                      type: h.honey_type || 'Unknown',
                      frames: h.frames_harvested || 0,
                      yard: h.yard || '',
                      fullDate: h.date,
                      honeyType: h.honey_type || 'Unknown',
                      revenue: ((parseFloat(h.quantity) || 0) * (parseFloat(h.sale_price) || 0))
                    }))}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} />
                    <YAxis tick={{ fontSize: 11 }} label={{ value: wUnit, angle: -90, position: 'insideLeft', fontSize: 11 }} />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white border border-stone-200 rounded-lg p-3 shadow-lg text-sm">
                              <div className="font-semibold text-stone-800 mb-1">{data.fullDate}</div>
                              <div className="text-amber-700 font-bold text-lg">{data.quantity} {wUnit}</div>
                              <div className="text-stone-600">{data.type}</div>
                              {data.frames > 0 && <div className="text-stone-500">{data.frames} frames</div>}
                              {data.yard && <div className="text-stone-400 text-xs">{data.yard}</div>}
                              {data.revenue > 0 && <div className="text-green-600 font-medium mt-1">Revenue: ${data.revenue.toFixed(2)}</div>}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="quantity" name={`Harvest (${wUnit})`} radius={[4, 4, 0, 0]}>
                      {harvests.slice(0, 12).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={YEAR_COLORS[index % YEAR_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            
            {/* Season Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-amber-700">{currentSeasonHarvests.length}</div>
                <div className="text-xs text-amber-600">This Season</div>
              </div>
              <div className="bg-stone-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-stone-700">{harvests.length}</div>
                <div className="text-xs text-stone-500">Total Harvests</div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-yellow-700">{allTimeFrames}</div>
                <div className="text-xs text-yellow-600">Frames Harvested</div>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      </div>

      {/* Harvest History */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm">
        <div className="p-4 border-b border-stone-100 flex items-center justify-between">
          <h3 className="font-semibold text-stone-800 flex items-center gap-2">
            🍯 Harvest History
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
              {harvests.length} records
            </span>
          </h3>
          <Select value={selectedYear || 'all'} onValueChange={(v) => setSelectedYear(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="harvest-year-filter-select" className="w-32 h-8">
              <SelectValue placeholder="All Years" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {years.map(year => (
                <SelectItem key={year} value={year}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="divide-y divide-stone-100 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-stone-400">Loading harvests...</div>
          ) : harvests.length === 0 ? (
            <div className="p-8 text-center text-stone-400">
              {yardFilter !== 'all' ? `No harvests recorded for ${yardFilter}${selectedYear ? ` in ${selectedYear}` : ''}.` : <>No harvests recorded yet. Click &ldquo;Record Harvest&rdquo; to add your first harvest.</>}
            </div>
          ) : (
            harvests.map(harvest => (
              <div key={harvest.id} className="p-4 hover:bg-stone-50 transition-colors" data-testid={`harvest-${harvest.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    {/* Main Info Row */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-stone-500 text-sm">{formatDate(harvest.date)}</span>
                      <span className="font-bold text-amber-700 text-lg">{harvest.quantity} {harvest.unit}</span>
                      {(harvest.yard || hiveYard(harvest.hive_ids?.[0] || harvest.hive_id)) && (
                        <span data-testid={`harvest-apiary-${harvest.id}`} className="text-stone-500 flex items-center gap-1 text-sm">
                          <MapPin className="h-3 w-3" />
                          {harvest.yard || hiveYard(harvest.hive_ids?.[0] || harvest.hive_id)}
                        </span>
                      )}
                      {harvest.frames_harvested > 0 && (
                        <span className="text-stone-400 text-sm">{harvest.frames_harvested} frames</span>
                      )}
                    </div>

                    {/* Tags Row */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {harvest.honey_type && (
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs">
                          {harvest.honey_type}
                        </span>
                      )}
                      {harvest.quality_grade && (
                        <span className="bg-stone-100 text-stone-600 px-2 py-0.5 rounded text-xs">
                          {harvest.quality_grade}
                        </span>
                      )}
                      {(harvest.hive_numbers?.length > 0 || harvest.hive_number) && (
                        <span className="text-stone-400 text-xs">
                          Hives: #{(harvest.hive_numbers?.length > 0 ? harvest.hive_numbers : [harvest.hive_number]).join(', #')}
                        </span>
                      )}
                    </div>

                    {/* Notes */}
                    {harvest.notes && (
                      <p className="text-sm text-stone-500 italic">&ldquo;{harvest.notes}&rdquo;</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-stone-400 hover:text-blue-600"
                      onClick={() => openEditDialog(harvest)}
                      data-testid={`edit-harvest-${harvest.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-stone-400 hover:text-red-600"
                      onClick={() => handleDelete(harvest.id)}
                      data-testid={`delete-harvest-${harvest.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Edit Harvest Dialog */}
      <Dialog open={!!editingHarvest} onOpenChange={(open) => !open && setEditingHarvest(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5" />
              Edit Harvest
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Quantity & Unit */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={editFormData.quantity || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, quantity: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Unit</Label>
                <Select value={editFormData.unit || 'kg'} onValueChange={(v) => setEditFormData({ ...editFormData, unit: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="lbs">lbs</SelectItem>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="oz">oz</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Honey Type & Quality */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Honey Type</Label>
                <Select 
                  value={editFormData.honey_type || ''} 
                  onValueChange={(v) => {
                    const selectedType = honeyTypes.find(t => t.name === v);
                    setEditFormData({ 
                      ...editFormData, 
                      honey_type: v,
                      sale_price: selectedType?.price_per_unit || editFormData.sale_price
                    });
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {honeyTypes.map(type => (
                      <SelectItem key={type.id || type.name} value={type.name}>
                        {type.name} {type.price_per_unit > 0 && `($${type.price_per_unit})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quality Grade</Label>
                <Select value={editFormData.quality_grade || ''} onValueChange={(v) => setEditFormData({ ...editFormData, quality_grade: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {qualityGrades.map(grade => (
                      <SelectItem key={grade.id || grade.name} value={grade.name}>
                        {grade.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Storage Location */}
            <div>
              <Label>Storage Location</Label>
              <Input
                value={editFormData.storage_location || ''}
                onChange={(e) => setEditFormData({ ...editFormData, storage_location: e.target.value })}
                placeholder="Where is this honey stored?"
                className="mt-1"
              />
            </div>

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Input
                value={editFormData.notes || ''}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                placeholder="Any additional notes..."
                className="mt-1"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditingHarvest(null)}>Cancel</Button>
              <Button onClick={saveHarvestEdit} disabled={savingEdit} className="bg-amber-500 hover:bg-amber-600">
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record Harvest from Pending Dialog */}
      <Dialog open={!!recordingHarvest} onOpenChange={() => setRecordingHarvest(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-amber-600" />
              Record Harvest — Hive {recordingHarvest?.hive_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
              <span className="font-medium text-orange-800">{recordingHarvest?.quantity}x {recordingHarvest?.super_type}</span>
              <span className="text-orange-600 ml-2">removed {recordingHarvest?.date_removed && new Date(recordingHarvest.date_removed).toLocaleDateString()}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input type="date" value={recordFormData.date || ''} onChange={(e) => setRecordFormData({ ...recordFormData, date: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Year</Label>
                <Input value={recordFormData.harvest_year || ''} onChange={(e) => setRecordFormData({ ...recordFormData, harvest_year: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Quantity</Label>
                <Input type="number" step="0.1" value={recordFormData.quantity || ''} onChange={(e) => setRecordFormData({ ...recordFormData, quantity: e.target.value })} placeholder="0.0" className="mt-1" />
              </div>
              <div>
                <Label>Unit</Label>
                <Select value={recordFormData.unit || 'kg'} onValueChange={(v) => setRecordFormData({ ...recordFormData, unit: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="lbs">lbs</SelectItem>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="oz">oz</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Frames</Label>
                <Input type="number" value={recordFormData.frames_harvested || ''} onChange={(e) => setRecordFormData({ ...recordFormData, frames_harvested: e.target.value })} placeholder="#" className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Honey Type</Label>
                <Select value={recordFormData.honey_type || ''} onValueChange={(v) => setRecordFormData({ ...recordFormData, honey_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {honeyTypes.map(type => (
                      <SelectItem key={type.id || type.name} value={type.name}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quality</Label>
                <Select value={recordFormData.quality_grade || ''} onValueChange={(v) => setRecordFormData({ ...recordFormData, quality_grade: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {qualityGrades.map(g => (
                      <SelectItem key={g.id || g.name} value={g.name}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Price per unit</Label>
              <Input type="number" step="0.01" value={recordFormData.sale_price || ''} onChange={(e) => setRecordFormData({ ...recordFormData, sale_price: e.target.value })} placeholder="0.00" className="mt-1" />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={recordFormData.notes || ''} onChange={(e) => setRecordFormData({ ...recordFormData, notes: e.target.value })} className="mt-1" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setRecordingHarvest(null)}>Cancel</Button>
              <Button onClick={handleRecordHarvest} disabled={savingRecord} className="bg-amber-500 hover:bg-amber-600" data-testid="save-record-harvest-btn">
                {savingRecord ? 'Recording...' : 'Record Harvest'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HarvestsView;
